import { Status } from 'hooks/requests/fetchUtils';
import useAnalyticsVariables from 'hooks/useAnalyticVariables';
import useFetchApi from 'hooks/useFetchApi';
import useQueryParameters from 'hooks/useQueryParameters';
import { useCallback, useState } from 'react';
import useAnalytics, { Event } from 'hooks/useAnalytics';
import { UnifiedVirtualCardIssuanceResponse } from './unifiedVirtualCardIssuance';
import { mergeIssuance } from 'state/unifiedIssuance';
import { useDispatch } from 'react-redux';

const PATH = '/api/financing_virtual_card_issuance_status';

export interface UnifiedVirtualCardIssuanceStatusRequest {
  issuanceId: string;
}

type UnifiedVirtualCardIssuanceStatusHook = () => [
  (
    params: UnifiedVirtualCardIssuanceStatusRequest,
  ) => Promise<UnifiedVirtualCardIssuanceStatusResponse>,
  Status,
  boolean,
];

export type UnifiedVirtualCardIssuanceStatusResponse = UnifiedVirtualCardIssuanceResponse;

export class UnifiedVirtualCardIssuanceStatusError extends Error {}

const useUnifiedVirtualCardIssuanceStatus: UnifiedVirtualCardIssuanceStatusHook = () => {
  const [error, setError] = useState<boolean>(false);
  const [status, fetch] = useFetchApi();
  const { sendEvents } = useAnalytics();
  const { mockVCIS } = useQueryParameters();
  const { setAnalyticsVariables } = useAnalyticsVariables();
  const dispatch = useDispatch();

  const unifiedVirtualCardIssuanceStatusRequest = useCallback(
    async (
      params: UnifiedVirtualCardIssuanceStatusRequest,
    ): Promise<UnifiedVirtualCardIssuanceStatusResponse> => {
      setError(false);

      const responsePromise = fetch(PATH, {
        method: 'POST',
        body: {
          ...params,
          mockResponse: mockVCIS,
        },
      });

      sendEvents([Event.IssuanceStatusPolled]);
      const response = await responsePromise;

      if (response.ok) {
        const result: UnifiedVirtualCardIssuanceStatusResponse = await response.json();
        dispatch(
          mergeIssuance({
            issuanceId: result.issuanceId,
            hasWallets: result.hasWallets,
          }),
        );
        setAnalyticsVariables({
          virtualCardIssuanceStatus: result.issuanceStatus,
          virtualCardStatus: result.cardStatus,
        });
        return Promise.resolve(result);
      } else {
        setError(true);
        return Promise.reject(new UnifiedVirtualCardIssuanceStatusError());
      }
    },
    [fetch, mockVCIS, setAnalyticsVariables, sendEvents, dispatch],
  );

  return [unifiedVirtualCardIssuanceStatusRequest, status, error];
};

export default useUnifiedVirtualCardIssuanceStatus;
